/**
 * Volume by Price — a histogram over *price* rather than over time.
 *
 * Horizontal bars down one edge of the price pane. Each spans a slice of price
 * over the chosen period, and is as long as the volume that traded while price
 * closed inside that slice, split into the part from up days and the part from
 * down days.
 *
 * **The slice is as tall as the symbol's own volatility, and it sits on an
 * absolute price grid.** That is the one thing here that is not StockCharts':
 * their zones are the close range cut into N, which makes every edge a function
 * of the window — one new high moves all of them — so a level read yesterday is
 * not the same level today. Three quarters of an average day's true range,
 * snapped to a round increment and anchored to prices rather than to the lowest
 * close, gives a grid that a new bar extends and never shifts. `bands: "fixed"`
 * is the original and stays a choice.
 *
 * **This is not a volume profile, and the difference is the whole reason the
 * feature is small.** A profile bins by the price a share actually *traded* at,
 * which a daily bar does not record — it carries one volume and a high-low
 * range and says nothing about the distribution between them, so a profile on
 * daily data has to assume a shape (uniform, close-weighted, OHLC-split) and
 * then looks authoritative while resting on that assumption. Volume-by-Price is
 * StockCharts' method and is defined on closing prices — "highs and lows are
 * omitted" — so a bar's whole volume goes into the zone containing its close.
 * Nothing here is approximated and no distribution is assumed, which is why the
 * indicator is computable from the daily OHLCV this store already holds.
 *
 * **It computes on the client, which makes it the first indicator qv-data does
 * not own.** Not because of the viewport — the period is a fixed lookback, and
 * a server knows the last N bars perfectly well — but because the *output is the
 * wrong shape for the catalog*. Every entry's `compute` returns `list[pl.Expr]`,
 * one value per bar; this produces a dozen rows of `(low, high, up, down)` whose
 * zone edges are themselves derived from the data. Declaring twelve outputs
 * instead would pin the bin count into the contract and still not carry the
 * edges. That argument holds for any period and any bin count, where the
 * viewport one would have been undone by the very change to a lookback.
 *
 * Everything decidable about the indicator is in this file. The renderer needs
 * WebGL and a WASM engine jsdom does not have, so the shell is verified by eye
 * or not at all — which is the reason to leave nothing here for it to decide.
 *
 * Design: docs/volume-by-price-design.html.
 */

/** One zone of the price range, and the volume that closed inside it. */
export interface VolumeBin {
  /** Zone edges in price. `low` inclusive, `high` exclusive except in the last zone. */
  low: number;
  high: number;
  /** Volume from bars that closed up / down, in this zone. */
  up: number;
  down: number;
  /**
   * `up + down`, written once when the zone is finished.
   *
   * Derived rather than stored would be the purer choice; it is here because
   * every reader of a zone wants the total and two of them were computing it
   * inline, and because a caller that only wants "how much traded here" should
   * not have to know the split exists.
   */
  volume: number;
}

export interface VolumeByPricePlan {
  bins: VolumeBin[];
  /** The largest `up + down`, so the shell scales bar lengths without a second pass. */
  peak: number;
  /** The period actually used — fewer than `lookback` when the series is short. */
  barCount: number;
  /**
   * The ATR grid this plan was binned on — absent for the `fixed` mode, which
   * has no grid: its zones are the close range cut into N, and nothing about
   * them survives the next bar.
   */
  width?: number;
  gridLow?: number;
  gridHigh?: number;
  bandCount?: number;
  /** Mean (or median) TR% over the ATR window, in price. */
  atrValue?: number;
  /**
   * Set when ATR sizing was asked for and could not be done — too little
   * history, or a period with no price range at all — and the fixed-count
   * binner answered instead. The picture is then exactly the `fixed` mode's,
   * which is the point: a new listing draws something rather than nothing.
   */
  fallbackUsed?: boolean;
}

export interface VolumeByPriceOptions {
  lookback: number;
  bins: number;
  /**
   * An explicit half-open bar range `[from, to)`, which **overrides `lookback`**.
   *
   * This is the `visible` period: the bars currently on screen. It is a range of
   * indices rather than a pre-sliced array for the same reason the whole series
   * is passed in the first place — the first bar of the period takes its
   * direction from the bar *before* it, and a slice throws that predecessor
   * away.
   */
  range?: { from: number; to: number };
}

/**
 * Two years of sessions.
 *
 * A *lookback* rather than the visible window — which is the departure from
 * StockCharts, and the better reading: a support level that slides while you pan
 * is not a level. It also needs no new interaction, and it is what lets the
 * indicator recompute on a data change rather than on every frame of a pan.
 *
 * **It was 250 until ATR sizing arrived**, and the two changes belong together:
 * a fixed twelve zones over two years of prices is twelve very coarse zones,
 * while a grid whose width comes from the instrument's own volatility gets
 * finer as the period lengthens. The longer period is what makes the levels
 * worth having; the sizing is what keeps them readable.
 *
 * Only new plots take it. A stored alias carries its own number, so nothing on
 * an existing chart re-bins.
 */
export const DEFAULT_VBP_LOOKBACK = 500;

/**
 * StockCharts' own bin count — the `fixed` mode's zone count, and the count the
 * ATR mode falls back to when a symbol has too little history to size a grid
 * from. One number for both, because a fallback to a count nobody can see or
 * edit is a picture with no explanation on the picker.
 */
export const DEFAULT_VBP_BINS = 12;

/**
 * Share of the pane the longest bar fills.
 *
 * A readability number, picked by eye rather than derived — it shipped at 0.20
 * and moved to 0.30 once somebody had seen it against candles, which is the
 * only way this kind of value gets settled here.
 *
 * **Kept in step with the catalog's own default deliberately.** This is the
 * fallback for an alias with no width slot; the catalog's is what a fresh plot
 * mints. Letting them drift would make a stored plot draw a different size from
 * a new one for no reason a user could see.
 */
export const DEFAULT_VBP_WIDTH = 0.3;

/**
 * Which bars the profile is built from.
 *
 * `lookback` holds still while you pan, which is what makes a level worth
 * drawing on. `visible` is StockCharts' own behaviour — the profile describes
 * exactly what is on screen and re-bins as you scroll, which reads as a live
 * answer to "where has business happened *here*" and is the opposite property.
 *
 * Neither is more correct; they answer different questions. `lookback` is the
 * default because it is the one that was specified, built and looked at first.
 */
export type VolumeByPricePeriod = "lookback" | "visible";

export const DEFAULT_VBP_PERIOD: VolumeByPricePeriod = "lookback";

/**
 * Which edge of the pane the bars grow from.
 *
 * **`right` by default, which is a deliberate departure from StockCharts.**
 * ChartSchool draws left; the user asked for right, and it is the better side
 * here for a reason the reference could not have: price is read at the right
 * edge on this chart — that is where the last bar, the value tags and the
 * crosshair readout all are — so a profile there sits beside the price it
 * describes rather than opposite it.
 *
 * Left remains a choice because the trade is real: on the right the bars share
 * an edge with the newest candles, which is exactly where a chart is busiest.
 */
export type VolumeByPriceSide = "left" | "right";

export const DEFAULT_VBP_SIDE: VolumeByPriceSide = "right";

/**
 * How the zone height is decided.
 *
 * `fixed` is the original and is StockCharts' own: cut the close range into N
 * equal slices. Its weakness is that the slices are a function of the *window* —
 * every zone edge is `lo + i * (hi - lo) / N`, so one new high moves all twelve
 * of them, and a level read yesterday is not the same level today. The picture
 * is stable only while the range is.
 *
 * `atr` sizes one zone from the instrument's own volatility and then lays those
 * zones on an **absolute price grid** — `…, 40.0, 42.5, 45.0, …` — rather than
 * on the window. Nothing about an edge depends on which bars are in the period,
 * so appending a bar, dropping the oldest, or panning in `visible` mode adds and
 * removes zones at the ends and moves none of the ones between. That is the
 * whole reason for it: a horizontal line worth trading against has to be in the
 * same place tomorrow, and no stored state is needed to keep it there.
 */
export type VolumeByPriceBands = "atr" | "fixed";

export const DEFAULT_VBP_BANDS: VolumeByPriceBands = "atr";

/**
 * Half a year of sessions of volatility, which is the window the zone height is
 * read from.
 *
 * Long enough that a single gap or earnings day cannot resize the grid, short
 * enough to follow a symbol that has changed character. It is deliberately **not**
 * tied to `lookback`: the period says which bars are counted, this says how big
 * a zone is, and letting one drive the other would make a longer period draw
 * coarser zones exactly where a user asked for more detail.
 */
export const VBP_ATR_PERIOD = 126;

/**
 * Zone height as a share of one average day's range.
 *
 * Three quarters of a day: small enough that a normal session cannot cross the
 * grid unnoticed, large enough that a zone still collects several closes over a
 * long period. Not editable, by decision — it is a shape constant, and a spinner
 * for it would be a fourth number to tune on a picker that already has five
 * controls.
 */
export const VBP_ATR_K = 0.75;

/**
 * The readable range of zone counts.
 *
 * Under fifteen the profile is a handful of slabs and says nothing about where
 * inside them the business was; over sixty the zones are thinner than the bars
 * are tall and the picture turns to noise. The clamp is applied to the
 * **lookback** period's grid even when the period being drawn is `visible`, so
 * the width is decided once per symbol and zooming never re-derives it.
 */
export const VBP_MIN_BANDS = 15;
export const VBP_MAX_BANDS = 60;

/**
 * The least history ATR sizing will work from.
 *
 * Below this a mean true range is a statement about four or five sessions, and a
 * grid built on it would be resized by the next one. A newer listing draws the
 * fixed-count picture instead and says so through `fallbackUsed` — the warm-up
 * is silent to the user, because the alternative is an empty pane on a symbol
 * whose only fault is being young.
 */
export const VBP_ATR_MIN_BARS = 20;

/**
 * A hard ceiling on the zones the plan may return, and it exists for the
 * renderer rather than for the reader.
 *
 * `VBP_MAX_BANDS` bounds the *lookback* grid. In `visible` mode the window can
 * be panned across a price range far wider than the lookback's — a symbol that
 * went 20 to 3000 over its life — and the grid then spans it at the lookback's
 * width, which is honest but is also two box annotations per zone. This steps
 * the width up until the count fits. It is four times the readable maximum
 * because reaching it at all means the view is already showing a range nobody
 * reads levels off.
 */
const VBP_HARD_MAX_BANDS = VBP_MAX_BANDS * 4;

/**
 * The mantissas of a "nice" width, in the series `{1, 2, 2.5, 5} x 10^n`.
 *
 * **Snapping to these is what makes the grid stable without storing anything.**
 * A width taken straight from the ATR is a number like 2.3718; tomorrow's is
 * 2.3742, and every zone edge in the profile moves by a hair — invisible on the
 * screen and fatal to the claim that a level is the same level. Snapped, both
 * days give 2.5, and the grid is bit-for-bit identical until the volatility
 * actually changes enough to cross a rung.
 *
 * The rungs are the ones a price axis already uses, which is why a zone edge
 * tends to land on a round number a reader would have drawn anyway.
 */
const VBP_NICE_MANTISSAS = [1, 2, 2.5, 5] as const;

/**
 * One segment's x span, in the axis' relative 0-1 space.
 *
 * Takes the segment as a **distance from the chosen edge** — `from` and `to`
 * measured inward — and returns the x pair the renderer wants. That framing is
 * what keeps the two sides from being two implementations: the up segment is
 * always nearest the edge and the down segment always beyond it, whichever edge
 * that is, so the caller stacks them once and this mirrors.
 *
 * Pure and here rather than in the shell for the reason the binning is: a
 * mirror that is subtly wrong draws a perfectly plausible profile pointing the
 * wrong way, and the shell is verified by eye or not at all.
 */
export function volumeBarX(
  from: number,
  to: number,
  side: VolumeByPriceSide,
): { x1: number; x2: number } {
  return side === "right" ? { x1: 1 - to, x2: 1 - from } : { x1: from, x2: to };
}

/**
 * The three parameters, read off the plot's own alias.
 *
 * **This is how every indicator carries its parameters**, and reading them here
 * rather than in the shell is the whole point: the shell cannot be unit-tested,
 * so a parameter parsed there is a parameter nobody can prove works. The first
 * build of this indicator bound the defaults directly in `paintVolumeByPrice`,
 * which meant the catalog declared three editable parameters, the picker drew
 * three spinners, Apply re-minted the alias and tore down the chart — and the
 * picture never changed. Everything was wired except the last inch.
 *
 * `volumeByPrice_250_12_20` — the family's own spelling, `name_p1_p2_p3`, the
 * same form `parse_alias` reads back server-side. **Width is a percentage in
 * the alias and a fraction here**, because the catalog parameter a person edits
 * is `20` and the renderer wants `0.2`; converting at the boundary means only
 * this function knows both.
 *
 * Anything unparseable falls back to the settled defaults rather than throwing.
 * A malformed alias should draw the indicator somebody asked for, not remove it
 * from the chart — the same rule the DSL follows for an unknown column.
 */
export function volumeByPriceParams(alias: string | undefined): {
  lookback: number;
  bins: number;
  width: number;
  period: VolumeByPricePeriod;
  side: VolumeByPriceSide;
  bands: VolumeByPriceBands;
} {
  const parts = (alias ?? "").split("_");
  const read = (at: number, fallback: number): number => {
    const value = Number(parts[at]);
    return Number.isFinite(value) && value > 0 ? value : fallback;
  };
  return {
    lookback: Math.floor(read(1, DEFAULT_VBP_LOOKBACK)),
    bins: Math.floor(read(2, DEFAULT_VBP_BINS)),
    // Clamped to the pane: a width over 100% would draw past the plot area, and
    // the spinner cannot stop a typed number.
    width: Math.min(1, read(3, DEFAULT_VBP_WIDTH * 100) / 100),
    side: parts[5] === "left" ? "left" : DEFAULT_VBP_SIDE,
    // **Appended, never inserted, and that is deliberate.** `period` arrived
    // after the indicator had already drawn on a real chart, so a stored
    // `volumeByPrice_250_12_20` is a plot somebody has. Reading it from the end
    // means every one of those still parses and lands on `lookback` — the
    // default, and what they were already seeing. Putting the choice first would
    // have been the better reading order and would have silently re-binned every
    // saved chart.
    period: parts[4] === "visible" ? "visible" : DEFAULT_VBP_PERIOD,
    // Seventh slot, appended for the reason `period` and `side` were, and the
    // cost of that rule is worth restating here because this parameter is the
    // one that changes the picture most: **an alias minted before this existed
    // lands on the new default**, so a stored `volumeByPrice_250_12_30` reloads
    // as an ATR grid rather than as the twelve zones it was drawing. That is
    // the decision — dynamic is the better picture and the one to give
    // everybody — and `fixed` is on the picker for anyone who disagrees.
    bands: parts[6] === "fixed" ? "fixed" : DEFAULT_VBP_BANDS,
  };
}

/** The one bar a series can hold whose direction nothing precedes. */
const FIRST_BAR_OF_HISTORY_IS_UP = true;

/**
 * Add a period's volume to zones that already exist, and report the tallest.
 *
 * **One copy of the direction rule, deliberately.** The zone a bar lands in is
 * the only thing the two sizing modes disagree about — `fixed` divides the
 * window, `atr` reads an absolute grid — and everything after that decision is
 * identical: where the previous close comes from, what an unchanged close counts
 * as, what a bar with no predecessor counts as, what a non-finite volume does.
 * Each of those is a rule somebody had to argue for once, and the second copy of
 * an argued rule is where the two copies start to differ.
 *
 * `indexOf` returns an unclamped zone index; the clamp is here because both
 * callers need exactly the same one — the period's own high lands one past the
 * end in both schemes, and 35 million shares landing nowhere is what the clamp
 * is actually for.
 */
function accumulate(
  bins: VolumeBin[],
  bars: readonly { close: number; volume: number }[],
  start: number,
  end: number,
  indexOf: (close: number) => number,
): number {
  let peak = 0;
  for (let i = start; i < end; i++) {
    const bar = bars[i];
    if (!bar || !Number.isFinite(bar.close)) continue;

    // A missing or non-finite volume contributes nothing rather than poisoning a
    // zone's total with NaN, which would take `peak` with it and collapse every
    // other bar to zero length.
    const volume = Number.isFinite(bar.volume) ? bar.volume : 0;

    const raw = indexOf(bar.close);
    const index = Math.min(bins.length - 1, Math.max(0, Number.isFinite(raw) ? raw : 0));
    const zone = bins[index];
    if (!zone) continue;

    // The direction comes from the bar before this one *in the loaded series*,
    // which for the period's own first bar is the bar before the period. Reading
    // the period's own predecessor instead would silently give that bar the
    // wrong direction whenever the chart has history behind the lookback — which
    // is always.
    const previous = bars[i - 1];
    const isUp =
      previous && Number.isFinite(previous.close)
        ? bar.close >= previous.close
        : FIRST_BAR_OF_HISTORY_IS_UP;

    if (isUp) zone.up += volume;
    else zone.down += volume;

    const total = zone.up + zone.down;
    zone.volume = total;
    if (total > peak) peak = total;
  }
  return peak;
}

/** The min and max close over a half-open bar range, ignoring gaps. */
function closeRange(
  bars: readonly { close: number }[],
  start: number,
  end: number,
): { lo: number; hi: number } {
  let lo = Infinity;
  let hi = -Infinity;
  for (let i = start; i < end; i++) {
    const close = bars[i]?.close;
    if (close === undefined || !Number.isFinite(close)) continue;
    if (close < lo) lo = close;
    if (close > hi) hi = close;
  }
  return { lo, hi };
}

/**
 * Bin the last `lookback` bars by closing price.
 *
 * **Bars are the WHOLE loaded series, oldest-first, not a pre-sliced period.**
 * That is not convenience: the first bar *of the period* takes its direction
 * from the bar *before* the period, and a caller that sliced first would have
 * thrown that bar away. The chart loads far more bars than any period asks for,
 * so the predecessor almost always exists; only at the very start of a symbol's
 * history is there none, and that one bar counts as up, because a zone silently
 * losing a day's volume is worse than an arbitrary tie-break.
 *
 * Returns bins low-to-high, so index 0 is the bottom of the pane.
 */
export function volumeByPrice(
  bars: readonly { close: number; volume: number }[],
  options: VolumeByPriceOptions,
): VolumeByPricePlan {
  // A bin count below one has no zone to put anything in, and a fractional one
  // would make `bins.length` disagree with the divisor in `width`. Floor and
  // floor at one rather than throw: this runs on every data change behind a
  // parameter a user can type into, and a degenerate picture beats a dead chart.
  const binCount = Math.max(1, Math.floor(options.bins));
  const lookback = Math.max(0, Math.floor(options.lookback));

  // **The period is a range, and a lookback is one way of naming it.**
  //
  // `range` is what the *visible* mode passes: the bars on screen, which move as
  // you pan. `lookback` is the other mode and is expressed in the same terms —
  // the last N bars — so both arrive here as a half-open `[start, end)` and
  // everything below is written once.
  //
  // Clamped rather than trusted: the range comes from a binary search over a
  // time axis that can be panned past either end of the data, so `from` beyond
  // the last bar and `to` past the end are both ordinary rather than a bug.
  const end = options.range
    ? Math.min(bars.length, Math.max(0, Math.floor(options.range.to)))
    : bars.length;
  const start = options.range
    ? Math.min(end, Math.max(0, Math.floor(options.range.from)))
    : Math.max(0, end - lookback);
  const period = bars.slice(start, end);

  const empty: VolumeByPricePlan = { bins: [], peak: 0, barCount: 0 };
  // A zero lookback means no period; an empty explicit range means the window
  // holds no bars, which happens when somebody pans clean off the data.
  if (period.length === 0 || (!options.range && lookback === 0)) return empty;

  // The range is over CLOSES — never highs and lows. This line is the method.
  const { lo, hi } = closeRange(bars, start, end);
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return empty;

  const span = hi - lo;
  // A flat period — one bar, or a halted symbol that closed at the same price
  // every day — has zero span, and `(close - lo) / 0` is NaN rather than a zone.
  // Every close is then the same price, so one zone of zero width holding all of
  // the volume is not a fallback, it is the correct answer.
  const width = span > 0 ? span / binCount : 0;

  const bins: VolumeBin[] = [];
  for (let i = 0; i < binCount; i++) {
    bins.push({
      low: lo + width * i,
      high: lo + width * (i + 1),
      up: 0,
      down: 0,
      volume: 0,
    });
  }
  // Floating-point multiplication does not land the last edge exactly on `hi`,
  // and this is the edge a reader checks against the price axis. Nothing depends
  // on it arithmetically — the clamp below is what actually places `hi` — but a
  // top zone labelled 612.9299999999998 against a high of 612.93 reads as a bug.
  const last = bins[bins.length - 1];
  if (last) last.high = hi;

  // The zone. `floor` puts a close on a boundary in the zone above it, so `low`
  // is inclusive and `high` exclusive — and the CLAMP inside `accumulate` is
  // what puts `hi` itself in the last zone rather than one past the end of the
  // array, which is where `floor((hi - lo) / width)` lands it exactly.
  const peak = accumulate(bins, bars, start, end, (close) =>
    width > 0 ? Math.floor((close - lo) / width) : 0,
  );

  return { bins, peak, barCount: period.length };
}

/**
 * A bar as the ATR sizing needs it: the close and volume the binning already
 * wanted, plus the extremes.
 *
 * **The extremes are optional and the binning still cannot see them.** They are
 * read by `atrBandGrid` and by nothing else — the method is defined on closes
 * and stays defined on closes, so this type widens the *sizing* input without
 * widening the input to `volumeByPrice`, whose parameter type having no field
 * for a high is the strongest available statement that it does not use one.
 *
 * A bar with no extremes degrades to `high = low = close`, which makes TR% the
 * close-to-close move. That is the honest answer for a series assembled from a
 * line rather than from candles, and it is a smaller width rather than a wrong
 * one.
 */
export interface VolumeByPriceBar {
  close: number;
  volume: number;
  high?: number;
  low?: number;
}

export interface VolumeByPriceAtrOptions extends VolumeByPriceOptions {
  /** Bars of volatility the width is read from. Defaults to {@link VBP_ATR_PERIOD}. */
  atrPeriod?: number;
  /** Zone height as a share of one average day's range. {@link VBP_ATR_K}. */
  k?: number;
  minBands?: number;
  maxBands?: number;
  /**
   * Take the **median** TR% rather than the mean.
   *
   * Not on the picker and not on the alias, by decision — it is here because
   * the difference is real and worth being able to reach: a mean is moved by a
   * single limit-up day or a gap through earnings, and on a symbol with a few
   * of those the grid is sized by its worst week rather than its normal one.
   * The median is the more robust statistic and the mean is the specified one,
   * so the mean is the default and this is one argument away.
   */
  median?: boolean;
}

/** The absolute grid a profile is laid on. */
export interface VolumeByPriceGrid {
  width: number;
  gridLow: number;
  gridHigh: number;
  bandCount: number;
  /** Mean (or median) TR% over the ATR window, in price. */
  atrValue: number;
}

/**
 * Twelve significant digits, which is where a double stops being able to lie
 * about a decimal price.
 *
 * `2.5 * 10 ** -1` is `0.25`, but `7 * 0.1` is `0.7000000000000001`, and a zone
 * edge is read against a price axis by a human being. Rounding here rather than
 * at the point of display keeps the *arithmetic* clean too: the grid's own
 * identity check — is this the same edge as yesterday — is an equality on these
 * numbers.
 */
function round12(value: number): number {
  return Number.isFinite(value) ? Number(value.toPrecision(12)) : value;
}

/**
 * The nearest nice width, chosen in log space.
 *
 * Log space rather than linear because the rungs are multiplicative: between 2
 * and 2.5 the linear midpoint is 2.25 and the log one is 2.236, and picking
 * linearly would bias every snap upward across the whole series. It also makes
 * the function scale-free — a $2 stock and a $3000 stock are snapped by the same
 * rule, which is the property that lets one constant `k` serve both.
 */
export function niceWidth(raw: number): number {
  if (!Number.isFinite(raw) || raw <= 0) return 0;
  const exponent = Math.floor(Math.log10(raw));
  const target = Math.log(raw);
  let best = 0;
  let bestDistance = Infinity;
  // One decade either side is enough: the series has a rung within a factor of
  // 2 of anything, so the nearest can never be two decades away.
  for (let e = exponent - 1; e <= exponent + 1; e++) {
    for (const mantissa of VBP_NICE_MANTISSAS) {
      const candidate = round12(mantissa * 10 ** e);
      if (!(candidate > 0)) continue;
      const distance = Math.abs(Math.log(candidate) - target);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = candidate;
      }
    }
  }
  return best;
}

/**
 * One rung up or down the nice series.
 *
 * Built as a ladder and indexed rather than computed from the mantissa, because
 * the step crosses a decade at both ends — 5 steps up to 10, 1 steps down to 0.5
 * — and every closed form for that is a special case waiting to be got wrong.
 */
export function stepNiceWidth(width: number, direction: 1 | -1): number {
  if (!Number.isFinite(width) || width <= 0) return 0;
  const exponent = Math.floor(Math.log10(width));
  const ladder: number[] = [];
  for (let e = exponent - 2; e <= exponent + 2; e++) {
    for (const mantissa of VBP_NICE_MANTISSAS) ladder.push(round12(mantissa * 10 ** e));
  }
  ladder.sort((a, b) => a - b);

  let at = 0;
  let bestDistance = Infinity;
  const target = Math.log(width);
  for (let i = 0; i < ladder.length; i++) {
    const distance = Math.abs(Math.log(ladder[i]!) - target);
    if (distance < bestDistance) {
      bestDistance = distance;
      at = i;
    }
  }
  // At the end of the ladder, stay put. The caller's guard reads "a step that
  // did not move" as "no further to go" and stops.
  return ladder[at + direction] ?? width;
}

/**
 * True range as a share of the close, one value per bar in `[from, to)`.
 *
 * **A percentage, not a price, and that is what makes one `k` work everywhere.**
 * A $3000 stock moving 1% has a $30 true range and a $2 stock moving 1% has two
 * cents; expressed as a share they are the same number, so the width formula
 * needs no per-price-scale tuning and a stock that went 20 to 100 inside the
 * lookback is sized by how it moves rather than by where it started.
 *
 * The first bar of a series has no previous close, so its true range is its own
 * high-low. That is the definition's own degenerate case rather than a guess.
 */
function truePercentRanges(
  bars: readonly VolumeByPriceBar[],
  from: number,
  to: number,
): number[] {
  const out: number[] = [];
  for (let i = from; i < to; i++) {
    const bar = bars[i];
    if (!bar || !Number.isFinite(bar.close) || bar.close <= 0) continue;
    const high = Number.isFinite(bar.high) ? bar.high! : bar.close;
    const low = Number.isFinite(bar.low) ? bar.low! : bar.close;
    const previous = bars[i - 1]?.close;
    const hasPrevious = previous !== undefined && Number.isFinite(previous);
    const range = Math.max(
      high - low,
      hasPrevious ? Math.abs(high - previous!) : 0,
      hasPrevious ? Math.abs(low - previous!) : 0,
    );
    if (!Number.isFinite(range) || range < 0) continue;
    out.push(range / bar.close);
  }
  return out;
}

function averageOf(values: readonly number[], median: boolean): number {
  if (values.length === 0) return 0;
  if (!median) return values.reduce((sum, v) => sum + v, 0) / values.length;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = sorted.length >> 1;
  return sorted.length % 2 === 1 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2;
}

/**
 * Which band of an absolute grid a price is in.
 *
 * **The whole floating-point defence is this function.** `floor(price / width)`
 * is correct arithmetic and wrong in a double: a close of `0.3` against a width
 * of `0.1` divides to `2.9999999999999996`, which floors to 2 — the band below
 * the one the price is sitting exactly on the floor of. The rule is that a price
 * on a boundary belongs to the band **above** it, so the quotient is snapped to
 * a whole number when it is within noise of one, and floored otherwise.
 *
 * The tolerance is absolute plus relative because the quotient grows with the
 * price: a $3000 stock on a 2.5 grid divides to 1200, where a double's own
 * spacing is already larger than a fixed 1e-9 would allow for.
 */
function bandIndexAt(price: number, width: number): number {
  if (!(width > 0)) return 0;
  const quotient = price / width;
  const nearest = Math.round(quotient);
  const tolerance = 1e-9 + Math.abs(nearest) * 1e-12;
  return Math.abs(quotient - nearest) <= tolerance ? nearest : Math.floor(quotient);
}

/** The band indices spanning `[lo, hi]` on a grid of this width. */
function gridBandsFor(
  width: number,
  lo: number,
  hi: number,
): { nLow: number; nHigh: number; bandCount: number } {
  const nLow = bandIndexAt(lo, width);
  // `+ 1` because the top close needs a band to sit in: it is the *floor* of the
  // band above it whenever it lands on a boundary, and inside the band below
  // otherwise. Either way that band is `bandIndexAt(hi) + 1` exclusive.
  const nHigh = bandIndexAt(hi, width) + 1;
  return { nLow, nHigh, bandCount: nHigh - nLow };
}

/**
 * The grid: one zone's height, and where the lattice starts.
 *
 * **Sized from the lookback bars even when the period being drawn is `visible`,
 * and that is the decision the mode turns on.** The width is a property of the
 * instrument — three quarters of how far it moves in a day — so it has no
 * business changing because somebody zoomed. Reading it from the window instead
 * would give the same symbol a different grid at every zoom level, the two
 * period modes would disagree with each other, and a level read at one scale
 * would not be there at the next.
 *
 * Returns `undefined` when it cannot honestly size one — too little history, or
 * a period whose closes have no range at all — and the caller draws the
 * fixed-count picture instead.
 */
export function atrBandGrid(
  bars: readonly VolumeByPriceBar[],
  options: VolumeByPriceAtrOptions,
): VolumeByPriceGrid | undefined {
  const atrPeriod = Math.max(1, Math.floor(options.atrPeriod ?? VBP_ATR_PERIOD));
  const k = options.k ?? VBP_ATR_K;
  const minBands = Math.max(1, Math.floor(options.minBands ?? VBP_MIN_BANDS));
  const maxBands = Math.max(minBands, Math.floor(options.maxBands ?? VBP_MAX_BANDS));
  const lookback = Math.max(1, Math.floor(options.lookback));

  // **The ATR window ends at the newest bar, always — never at the right edge of
  // a pan.** It is the last `atrPeriod` bars of the loaded series, so the answer
  // is the same whichever period mode is drawing and wherever the view happens
  // to be. Short history takes what exists, which is the warm-up: a listing four
  // months old is sized from four months.
  const end = bars.length;
  const atrFrom = Math.max(0, end - atrPeriod);
  if (end - atrFrom < VBP_ATR_MIN_BARS) return undefined;

  const ranges = truePercentRanges(bars, atrFrom, end);
  if (ranges.length < VBP_ATR_MIN_BARS) return undefined;

  let lastClose = NaN;
  for (let i = end - 1; i >= atrFrom; i--) {
    const close = bars[i]?.close;
    if (close !== undefined && Number.isFinite(close) && close > 0) {
      lastClose = close;
      break;
    }
  }
  if (!Number.isFinite(lastClose)) return undefined;

  const atrValue = averageOf(ranges, options.median ?? false) * lastClose;
  let width = niceWidth(k * atrValue);
  if (!(width > 0)) return undefined;

  // The clamp is measured against the LOOKBACK's range, not the drawn period's,
  // for the same reason the width is: it decides the grid once per symbol.
  const { lo, hi } = closeRange(bars, Math.max(0, end - lookback), end);
  if (!Number.isFinite(lo) || !Number.isFinite(hi) || !(hi > lo)) return undefined;

  // Step until the count is readable. The guard is not defensive: stepping down
  // can overshoot straight past `maxBands`, and without the check below the two
  // branches would hand the width back and forth forever.
  for (let guard = 0; guard < 32; guard++) {
    const bands = gridBandsFor(width, lo, hi);
    if (bands.bandCount > maxBands) {
      const wider = stepNiceWidth(width, 1);
      if (!(wider > width)) break;
      width = wider;
      continue;
    }
    if (bands.bandCount < minBands) {
      const finer = stepNiceWidth(width, -1);
      if (!(finer > 0) || finer >= width) break;
      // Overshoot: one rung finer is already too many zones. The coarser grid is
      // the better of the two answers, and stepping into it would oscillate.
      if (gridBandsFor(finer, lo, hi).bandCount > maxBands) break;
      width = finer;
      continue;
    }
    break;
  }

  const bands = gridBandsFor(width, lo, hi);
  return {
    width,
    gridLow: round12(bands.nLow * width),
    gridHigh: round12(bands.nHigh * width),
    bandCount: bands.bandCount,
    atrValue,
  };
}

/**
 * Bin a period onto the ATR grid.
 *
 * The grid's *width* comes from the lookback and the ATR window; the grid's
 * *extent* comes from the period actually being drawn, snapped to the same
 * lattice. In `lookback` mode those are the same bars and the two coincide. In
 * `visible` mode the window is a slice of the same lattice, which is what makes
 * panning add and drop zones at the ends rather than move the ones between.
 */
export function volumeByPriceAtr(
  bars: readonly VolumeByPriceBar[],
  options: VolumeByPriceAtrOptions,
): VolumeByPricePlan {
  const grid = atrBandGrid(bars, options);
  // Too little history, or no price range: the fixed-count picture, flagged. A
  // young listing draws something rather than nothing, and the count it draws is
  // the one on its own picker.
  if (!grid) return { ...volumeByPrice(bars, options), fallbackUsed: true };

  const lookback = Math.max(0, Math.floor(options.lookback));
  const end = options.range
    ? Math.min(bars.length, Math.max(0, Math.floor(options.range.to)))
    : bars.length;
  const start = options.range
    ? Math.min(end, Math.max(0, Math.floor(options.range.from)))
    : Math.max(0, end - lookback);

  const empty: VolumeByPricePlan = { bins: [], peak: 0, barCount: 0 };
  if (end - start <= 0 || (!options.range && lookback === 0)) return empty;

  const { lo, hi } = closeRange(bars, start, end);
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return empty;

  let width = grid.width;
  let bands = gridBandsFor(width, lo, hi);
  // The renderer's guard, not the reader's — see {@link VBP_HARD_MAX_BANDS}. It
  // only fires on a `visible` window panned across a range far wider than the
  // lookback's, and it is the one case where the grid does re-derive.
  for (let guard = 0; guard < 64 && bands.bandCount > VBP_HARD_MAX_BANDS; guard++) {
    const wider = stepNiceWidth(width, 1);
    if (!(wider > width)) break;
    width = wider;
    bands = gridBandsFor(width, lo, hi);
  }

  const bandCount = Math.max(1, bands.bandCount);
  const bins: VolumeBin[] = [];
  for (let i = 0; i < bandCount; i++) {
    // **Edges are computed from the band's own integer index**, never by adding
    // a width to the edge below. Accumulating would drift — a hundred additions
    // of 0.1 is not ten — and drift is exactly the failure this grid exists to
    // rule out.
    bins.push({
      low: round12((bands.nLow + i) * width),
      high: round12((bands.nLow + i + 1) * width),
      up: 0,
      down: 0,
      volume: 0,
    });
  }

  const peak = accumulate(bins, bars, start, end, (close) =>
    bandIndexAt(close, width) - bands.nLow,
  );

  return {
    bins,
    peak,
    barCount: end - start,
    width,
    gridLow: round12(bands.nLow * width),
    gridHigh: round12(bands.nHigh * width),
    bandCount,
    atrValue: grid.atrValue,
    fallbackUsed: false,
  };
}

/**
 * The indicator, either way round — the one function the renderer calls.
 *
 * The mode lives on the plot's alias and arrives here as a parameter, so the
 * shell picks nothing: it reads the alias, passes what it read, and draws what
 * comes back. That is the rule this file was built on and the defect it was
 * built after — a parameter decided in the shell is a parameter no test can
 * reach.
 */
export function volumeByPricePlan(
  bars: readonly VolumeByPriceBar[],
  options: VolumeByPriceAtrOptions & { bands?: VolumeByPriceBands },
): VolumeByPricePlan {
  return (options.bands ?? DEFAULT_VBP_BANDS) === "fixed"
    ? volumeByPrice(bars, options)
    : volumeByPriceAtr(bars, options);
}

/**
 * The bars inside a time window, as a half-open index range.
 *
 * **Pure, and here rather than in the shell, because it is the one piece of the
 * `visible` mode that can be got wrong quietly.** An off-by-one at either edge
 * drops or doubles a day's volume, and the picture stays entirely plausible — a
 * profile that is subtly the wrong shape looks exactly like a profile.
 *
 * `xs` is ascending bar timestamps; `window` is what the axis reports, which is
 * in the same units. Both edges are **inclusive of a bar whose time falls on
 * them**: a window snapped to a bar should contain that bar, and SciChart hands
 * back exactly the range it was set to.
 *
 * A window entirely off the data returns an empty range rather than a clamped
 * one-bar range — panning past the end should empty the profile, not pin it to
 * the last bar and pretend.
 */
export function visibleBarRange(
  xs: readonly number[],
  window: { min: number; max: number } | undefined,
): { from: number; to: number } | undefined {
  if (!window || xs.length === 0) return undefined;
  const { min, max } = window;
  if (!Number.isFinite(min) || !Number.isFinite(max) || max < min) return undefined;

  // First index with xs[i] >= min.
  let lo = 0;
  let hi = xs.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if ((xs[mid] ?? 0) < min) lo = mid + 1;
    else hi = mid;
  }
  const from = lo;

  // First index with xs[i] > max — the half-open end.
  lo = from;
  hi = xs.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if ((xs[mid] ?? 0) <= max) lo = mid + 1;
    else hi = mid;
  }
  return { from, to: lo };
}
